import { CampaignState } from 'modules/domains/campaign';
import { FC, useCallback } from 'react';
import { SubmitHandler, useFormContext } from 'react-hook-form';
import { BUTTON_SIZES, Button } from 'ui/button';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { ModalType, useCampaignContext, useModalContext } from '../../context';
import { ScheduleFormState } from '../../types';
import { useScheduleCampaign, useSendTimeIsWithinQuietHours } from './hooks';

export const SCHEDULE_CAMPAIGN_MODAL_TEST_ID = 'schedule-campaign-modal';

export const ScheduleModal: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const {
    handleSubmit,
    formState: { isValid, isDirty },
  } = useFormContext<ScheduleFormState>();

  const { campaign } = useCampaignContext();
  const { closeModal, openModal, openedModals } = useModalContext();
  const { isLoading, scheduleCampaign } = useScheduleCampaign();
  const { campaignState } = campaign;

  const { isSendTimeWithinQuietHoursAndSkipped } = useSendTimeIsWithinQuietHours();
  const isCurrentModalScheduleModal =
    openedModals?.[openedModals.length - 1].type === ModalType.ScheduleCampaign;
  /**
   * Submit handler to be called by react-hook-form's `handleSubmit`
   *
   * WARNING: `values` here is expected to be only the values of the form fields mounted/displayed on the modal.
   * @see https://react-hook-form.com/api/useform/handlesubmit
   * @see https://react-hook-form.com/api/useform#:~:text=CODESANDBOX-,shouldUnregister,-%3A%20boolean%20%3D%20false
   */
  const handleSchedule: SubmitHandler<ScheduleFormState> = useCallback(
    (values) => {
      openModal(ModalType.ConfirmScheduleCampaign, {
        onConfirm: () => scheduleCampaign(values),
        values,
        isLoading,
      });
    },
    [scheduleCampaign],
  );
  return (
    <Modal
      withPortal
      isModalOpen
      id="schedule-modal"
      data-test={SCHEDULE_CAMPAIGN_MODAL_TEST_ID}
      onClose={() => {
        if (isCurrentModalScheduleModal) {
          if (!isDirty) {
            closeModal(ModalType.ScheduleCampaign);
          } else {
            openModal(ModalType.ScheduleUnsavedChangesModal);
          }
        }
      }}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>Schedule campaign</ModalHeader>
      <ModalBody>{children}</ModalBody>
      <ModalFooter>
        <Button
          isDisabled={
            !isValid ||
            isSendTimeWithinQuietHoursAndSkipped ||
            /** For scheduled campaigns, we are disallowing updates if no changes have been made to the saved schedule */
            (campaignState === CampaignState.Scheduled && !isDirty)
          }
          onClick={handleSubmit(handleSchedule)}
          data-test={`${SCHEDULE_CAMPAIGN_MODAL_TEST_ID}-button`}
          isLoading={isLoading}
          size={BUTTON_SIZES.LARGE}
        >
          {campaignState === CampaignState.Scheduled ? 'Reschedule' : 'Schedule'} campaign
        </Button>
      </ModalFooter>
    </Modal>
  );
};
